import { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import apiClient from "../../services/apiClient";
import {
  RiseOutlined,
  FallOutlined,
  DollarOutlined,
  BarChartOutlined,
  EyeOutlined,
  StarOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  TrophyOutlined,
  GlobalOutlined,
  BankOutlined,
  LineChartOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
  BellOutlined,
  CalendarOutlined,
  PieChartOutlined,
  TrendingUpOutlined,
  AuditOutlined,
  BookOutlined,
  NotificationOutlined,
  DashboardOutlined,
  RocketOutlined,
  CrownOutlined,
  BulbOutlined
} from "@ant-design/icons";
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Button, 
  Typography, 
  Space, 
  Tag, 
  Progress, 
  Avatar, 
  List, 
  Badge, 
  Divider, 
  Tooltip,
  Alert,
  Spin,
  message,
  Timeline,
  Table,
  Select,
  DatePicker,
  Input,
  Dropdown,
  Menu
} from "antd";
import TwoFactorAuthPopup from "../TwoFactorAuthPopup";
import { ThemeContext } from "../../context/ThemeContext";
import { useUser } from "../../context/UserContext";
import "./Dashboard.css";

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

const InternalUserDashboard = () => {
  const location = useLocation();
  const { isDark } = useContext(ThemeContext);
  const { userData, loading } = useUser();
  const [show2FAPopup, setShow2FAPopup] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [twoFAMethods, setTwoFAMethods] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    totalIssuers: 0,
    totalISINs: 0,
    activeBonds: 0,
    pendingApprovals: 0,
    recentActivities: [],
    marketTrends: [],
    alerts: []
  });
  const [loadingData, setLoadingData] = useState(true);

  // Persist twoFAHandled in localStorage
  const [twoFAHandled, setTwoFAHandled] = useState(() => {
    return localStorage.getItem("twoFAHandled") === "true";
  });

  // Apply theme to document body
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Check 2FA status when component mounts
  useEffect(() => {
    check2FAStatus();
  }, []);

  const loadDashboardData = async () => {
    setLoadingData(true);
    try {
      // Simulate API calls for dashboard data
      // In real implementation, these would be actual API calls
      const mockData = {
        totalIssuers: 1247,
        totalISINs: 8934,
        activeBonds: 567,
        pendingApprovals: 23,
        recentActivities: [
          { id: 1, type: 'issuer', action: 'New issuer registered', issuer: 'ABC Corporation', time: '2 hours ago', status: 'completed' },
          { id: 2, type: 'isin', action: 'ISIN updated', isin: 'INE123456789', time: '4 hours ago', status: 'completed' },
          { id: 3, type: 'approval', action: 'Bond approval pending', issuer: 'XYZ Limited', time: '6 hours ago', status: 'pending' },
          { id: 4, type: 'rating', action: 'Credit rating updated', issuer: 'DEF Bank', time: '1 day ago', status: 'completed' },
          { id: 5, type: 'financial', action: 'Financial data uploaded', issuer: 'GHI Finance', time: '2 days ago', status: 'completed' }
        ],
        marketTrends: [
          { month: 'Jan', corporate: 8.5, government: 6.2, total: 7.3 },
          { month: 'Feb', corporate: 8.8, government: 6.5, total: 7.6 },
          { month: 'Mar', corporate: 9.1, government: 6.8, total: 7.9 },
          { month: 'Apr', corporate: 8.9, government: 6.6, total: 7.7 },
          { month: 'May', corporate: 9.3, government: 7.0, total: 8.1 },
          { month: 'Jun', corporate: 9.5, government: 7.2, total: 8.3 }
        ],
        alerts: [
          { id: 1, type: 'warning', message: '5 ISINs approaching maturity date', count: 5 },
          { id: 2, type: 'info', message: 'New regulatory compliance update available', count: 1 },
          { id: 3, type: 'success', message: 'System maintenance completed successfully', count: 1 }
        ]
      };
      
      setDashboardData(mockData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      message.error('Failed to load dashboard data');
    } finally {
      setLoadingData(false);
    }
  };

  const check2FAStatus = async () => {
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "http://localhost:8080/api";

      const response = await apiClient.get(`${baseUrl}/2fa/status`);

      console.log("2FA Status:", response.data);

      const statusData = response.data.success && response.data.data ? response.data.data : response.data;

      if (statusData.methods && statusData.methods.length === 0) {
        const popupShown = localStorage.getItem("2faPopupShown");
        if (!popupShown) {
          setShow2FAPopup(true);
          const userEmail = localStorage.getItem("userEmail") || "";
          setUserEmail(userEmail);
          await fetchTwoFAMethods();
          localStorage.setItem("2faPopupShown", "true");
        }
      } else {
        setShow2FAPopup(false);
      }
    } catch (error) {
      console.error("Error checking 2FA status:", error);
    }
  };

  const fetchTwoFAMethods = async () => {
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "http://localhost:8080/api";

      const response = await apiClient.get(`${baseUrl}/2fa/methods`);

      setTwoFAMethods(response.data.methods || []);
    } catch (error) {
      console.error("Error fetching 2FA methods:", error);
    }
  };

  const handle2FASetup = async (method) => {
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "http://localhost:8080/api";

      const response = await apiClient.post(
        `${baseUrl}/2fa/setup`,
        { method }
      );

      if (response.data.success) {
        message.success("2FA setup initiated successfully");
        setShow2FAPopup(false);
        setTwoFAHandled(true);
        localStorage.setItem("twoFAHandled", "true");
      }
    } catch (error) {
      console.error("Error setting up 2FA:", error);
      message.error("Failed to setup 2FA");
    }
  };

  const handle2FASkip = () => {
    setShow2FAPopup(false);
    setTwoFAHandled(true);
    localStorage.setItem("twoFAHandled", "true");
    message.info("2FA setup skipped. You can enable it later in settings.");
  };

  const getActivityIcon = (type) => {
    const icons = {
      issuer: <BankOutlined />,
      isin: <FileTextOutlined />,
      approval: <CheckCircleOutlined />,
      rating: <StarOutlined />,
      financial: <BarChartOutlined />
    };
    return icons[type] || <NotificationOutlined />;
  };

  const getActivityColor = (type) => {
    const colors = {
      issuer: 'blue',
      isin: 'green',
      approval: 'orange',
      rating: 'purple',
      financial: 'cyan'
    };
    return colors[type] || 'default';
  };

  const getAlertIcon = (type) => {
    const icons = {
      warning: <ExclamationCircleOutlined />,
      info: <BellOutlined />,
      success: <CheckCircleOutlined />
    };
    return icons[type] || <NotificationOutlined />;
  };

  const getAlertColor = (type) => {
    const colors = {
      warning: 'orange',
      info: 'blue',
      success: 'green'
    };
    return colors[type] || 'default';
  };

  const quickActions = [
    { title: 'View Issuers', icon: <BankOutlined />, path: '/d/issuer-list', color: '#1890ff' },
    { title: 'Browse ISINs', icon: <FileTextOutlined />, path: '/d/isin-list', color: '#52c41a' },
    { title: 'Financial Reports', icon: <BarChartOutlined />, path: '/d/issuer-financial-reports', color: '#722ed1' },
    { title: 'Market Data', icon: <LineChartOutlined />, path: '/d/nsdl', color: '#fa8c16' },
    { title: 'Ratings', icon: <TrophyOutlined />, path: '/d/ratings', color: '#eb2f96' },
    { title: 'News & Updates', icon: <GlobalOutlined />, path: '/d/news', color: '#13c2c2' }
  ];

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spin size="large" />
        <Text>Loading dashboard...</Text>
      </div>
    );
  }

  return (
    <div className="internal-dashboard">
      {/* 2FA Popup */}
      {show2FAPopup && (
        <TwoFactorAuthPopup
          visible={show2FAPopup}
          onSetup={handle2FASetup}
          onSkip={handle2FASkip}
          methods={twoFAMethods}
          userEmail={userEmail}
        />
      )}

      {/* Header Section */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="welcome-section">
            <Title level={2} className="welcome-title">
              Welcome back, {userData.firstName || userData.username || 'User'}! 👋
            </Title>
            <Text className="welcome-subtitle">
              Here's what's happening in your bond market dashboard today
            </Text>
          </div>
          <div className="header-actions">
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={loadDashboardData}
                loading={loadingData}
              >
                Refresh
              </Button>
              <Button icon={<SettingOutlined />} type="primary">
                Settings
              </Button>
            </Space>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <Row gutter={[24, 24]} className="metrics-section">
        <Col xs={24} sm={12} lg={6}>
          <Card className="metric-card">
            <Statistic
              title="Total Issuers"
              value={dashboardData.totalIssuers}
              prefix={<BankOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div className="metric-trend">
              <RiseOutlined style={{ color: '#52c41a' }} />
              <Text type="secondary">+12% from last month</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="metric-card">
            <Statistic
              title="Total ISINs"
              value={dashboardData.totalISINs}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div className="metric-trend">
              <RiseOutlined style={{ color: '#52c41a' }} />
              <Text type="secondary">+8% from last month</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="metric-card">
            <Statistic
              title="Active Bonds"
              value={dashboardData.activeBonds}
              prefix={<SafetyOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div className="metric-trend">
              <RiseOutlined style={{ color: '#52c41a' }} />
              <Text type="secondary">+5% from last month</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="metric-card">
            <Statistic
              title="Pending Approvals"
              value={dashboardData.pendingApprovals}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
            <div className="metric-trend">
              <FallOutlined style={{ color: '#ff4d4f' }} />
              <Text type="secondary">-3 from yesterday</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Quick Actions - Full Width */}
      <Row gutter={[24, 24]} className="quick-actions-section" style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <Card title="Quick Actions" className="quick-actions-card">
            <div className="quick-actions-grid">
              {quickActions.map((action, index) => (
                <Button
                  key={index}
                  className="quick-action-btn"
                  icon={action.icon}
                  onClick={() => window.location.href = action.path}
                  style={{ borderColor: action.color, color: action.color }}
                >
                  {action.title}
                </Button>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={[24, 24]} className="main-content">
        {/* Recent Activities */}
        <Col xs={24}>
          <Card title="Recent Activities" className="activities-card">
            <Timeline>
              {dashboardData.recentActivities.map((activity) => (
                <Timeline.Item
                  key={activity.id}
                  dot={
                    <Avatar 
                      icon={getActivityIcon(activity.type)} 
                      size="small"
                      style={{ backgroundColor: getActivityColor(activity.type) }}
                    />
                  }
                >
                  <div className="activity-item">
                    <div className="activity-content">
                      <Text strong>{activity.action}</Text>
                      <br />
                      <Text type="secondary">
                        {activity.issuer || activity.isin} • {activity.time}
                      </Text>
                    </div>
                    <Tag color={activity.status === 'completed' ? 'green' : 'orange'}>
                      {activity.status}
                    </Tag>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Col>
      </Row>

      {/* Market Trends */}
      <Row gutter={[24, 24]} className="trends-section" style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card title="Market Yield Trends" className="trends-card">
            <Row gutter={[16, 16]}>
              {dashboardData.marketTrends.slice(-3).map((trend, index) => (
                <Col xs={24} sm={8} key={index}>
                  <Card size="small" className="trend-card">
                    <Statistic
                      title={trend.month}
                      value={trend.total}
                      suffix="%"
                      precision={1}
                      valueStyle={{ color: '#1890ff' }}
                    />
                    <div className="trend-breakdown">
                      <Text type="secondary">Corporate: {trend.corporate}%</Text>
                      <br />
                      <Text type="secondary">Government: {trend.government}%</Text>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>

      {/* User Info Footer */}
      <div className="dashboard-footer">
        <Row justify="space-between" align="middle">
          <Col>
            <Text type="secondary">
              Logged in as: {userData.username} ({userData.roles?.join(', ') || 'No roles assigned'})
            </Text>
          </Col>
          <Col>
            <Text type="secondary">
              Last updated: {new Date().toLocaleString()}
            </Text>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default InternalUserDashboard;
